/**
 * Shared types for dashboard layout system.
 *
 * These types are used across:
 * - V1LayoutAdapter (grid-based layouts)
 * - migrations (version upgrades/downgrades)
 * - useDashboardLayout composable
 * - Vue components
 */

// ============================================================================
// Layout Version Constants
// ============================================================================

export const LAYOUT_VERSIONS = {
  V1: 1,
  V2: 2,
} as const;

export type LayoutVersion = (typeof LAYOUT_VERSIONS)[keyof typeof LAYOUT_VERSIONS];

// ============================================================================
// V1 Layout Types (Grid-based with x, y, w, h coordinates)
// ============================================================================

/**
 * A single item in a V1 grid layout.
 * Supports both snake_case (API) and camelCase (internal) property names.
 */
export interface LayoutItem {
  /** X coordinate (column position, 0-indexed) */
  x: number;
  /** Y coordinate (row position, 0-indexed) */
  y: number;
  /** Width in grid columns */
  w: number;
  /** Height in grid rows */
  h: number;
  /** Report type (e.g., 'TOTAL_METRIC', 'GRAPH') */
  type: string;
  /** Report ID (snake_case, from API) */
  report_id?: number;
  /** Report ID (camelCase, internal use) */
  reportId?: number;
  /** Unique identifier for vue3-grid-layout */
  i?: string;
  /** Whether item was moved (vue3-grid-layout) */
  moved?: boolean;
  /** Minimum width constraint */
  minW?: number;
  /** Maximum width constraint */
  maxW?: number;
  /** Minimum height constraint */
  minH?: number;
  /** Maximum height constraint */
  maxH?: number;
  /** Temporary ref into layout for bulk creation */
  client_ref?: string;
}

/**
 * Position coordinates for a layout item.
 */
export interface LayoutPosition {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * Result of finding a report in the layout.
 */
export interface LayoutReportInfo {
  index: number;
  reportId: number;
  type: string;
  position: LayoutPosition;
  raw: LayoutItem;
}

/**
 * Normalized report info from layout.
 */
export interface NormalizedReportInfo {
  reportId: number;
  type: string;
  position: LayoutPosition;
}

/**
 * Validation result for layout.
 */
export interface LayoutValidationResult {
  valid: boolean;
  errors: string[];
}

// ============================================================================
// V2 Layout Types (Row/cell-based with percentage widths)
// ============================================================================

/**
 * A cell in a V2 layout row.
 */
export interface LayoutV2Cell {
  /** Report metadata */
  meta: Record<string, unknown>;
  /** Report ID */
  reportId?: number;
  /** Report ID (snake_case, for API compatibility) */
  report_id?: number;
  /** Report type */
  type: string;
  /** Width as percentage (0-100) */
  width: number;
}

/**
 * A row in a V2 layout.
 */
export interface LayoutV2Row {
  cells: LayoutV2Cell[];
}

/**
 * V2 layout structure (row/cell based).
 */
export interface LayoutV2 {
  /** Optional identifier */
  id?: string;
  /** Layout version (should be '2' or 2) */
  version: string | number;
  /** Array of rows */
  rows: LayoutV2Row[];
}

// ============================================================================
// Height Configuration Types
// ============================================================================

/**
 * Height configuration for a report type.
 * Used by adjustLayoutForHeightChanges to handle height migrations.
 */
export interface HeightConfig {
  /** Current height in grid rows */
  height: number;
  /** Previous height (for backwards compatibility migrations) */
  oldHeight?: number;
}

/**
 * Function type for getting height config by report type.
 */
export type GetHeightConfigFn = (type: string) => HeightConfig;

// ============================================================================
// Report Configuration Types
// ============================================================================

/**
 * Configuration for a report type from reports.enum.
 */
export interface ReportConfig {
  /** Report width in grid columns */
  width: number;
  /** Report height in grid rows */
  height: number;
  /** Previous height for migration */
  oldHeight?: number;
  /** Report type value */
  value?: string;
  [key: string]: unknown;
}

// ============================================================================
// Utility Types
// ============================================================================

/**
 * Options for adding a report to the layout.
 */
export interface AddReportOptions {
  reportId?: number;
  clientRef?: string;
  type: string;
  /** Placement hint (ignored in V1, used in V2) */
  placement?: {
    rowIndex?: number;
    cellIndex?: number;
  };
}

/**
 * Union type for any layout format.
 */
export type AnyLayout = LayoutItem[] | LayoutV2;

/**
 * Helper to get the report ID from a layout item regardless of casing.
 */
export function getReportId(item: LayoutItem | LayoutV2Cell): number | undefined {
  return item.report_id ?? item.reportId;
}
